/** Multi-condition filter builder (Slice 9) in the dock — an AND/OR ("Match all / Match any") root
 * group of condition rows plus at most one level of nested grouping (Affinity parity). Emits an
 * opaque FilterGroup tree that CrmListGrid evaluates via AG's external filter + persists in the view.
 * The value input adapts to the operator: none for empties, a checkbox set for enum/ref "any of"
 * ops (falls back to a comma list), two inputs for `between`, else a single text/number/date input. */
import styles from './crm.module.css';
import type { CrmGridColumn } from '../../types';
import { enumDisplay } from './crmSystemFields';
import {
  NO_VALUE_OPS, SET_OPS, operatorsForType,
  type FilterCondition, type FilterGroup, type FilterOp,
} from './crmFilterTree';

const OP_LABEL: Record<FilterOp, string> = {
  contains: 'contains', not_contains: 'does not contain', is: 'is', is_not: 'is not',
  is_empty: 'is empty', is_not_empty: 'is not empty',
  is_any_of: 'is any of', is_none_of: 'is none of',
  has_any_of: 'has any of', has_all_of: 'has all of', has_none_of: 'has none of',
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between',
  before: 'before', after: 'after',
};

interface Props {
  columns: CrmGridColumn[];
  fieldOptions: Record<string, string[]>;
  tree: FilterGroup;
  onChange: (tree: FilterGroup) => void;
}

const emptyGroup = (logic: 'and' | 'or' = 'and'): FilterGroup => ({ kind: 'group', logic, children: [] });
const clone = (t: FilterGroup): FilterGroup => JSON.parse(JSON.stringify(t));

export function CrmFilterPanel({ columns, fieldOptions, tree, onChange }: Props) {
  const colByKey = new Map(columns.map((c) => [c.field_key, c]));
  const firstField = columns[0]?.field_key ?? '';

  const newCondition = (): FilterCondition => {
    const c = colByKey.get(firstField);
    return { kind: 'cond', field: firstField, op: operatorsForType(c?.field_type ?? 'text')[0], value: '' };
  };

  // group is either the root (path=[]) or a nested group (path=[i]); conditions live at path=[i] or [i,j]
  const setRootLogic = (logic: 'and' | 'or') => onChange({ ...clone(tree), logic });

  const mutate = (fn: (t: FilterGroup) => void) => { const t = clone(tree); fn(t); onChange(t); };

  const renderCondition = (c: FilterCondition, remove: () => void, update: (next: FilterCondition) => void) => {
    const col = colByKey.get(c.field);
    const ops = operatorsForType(col?.field_type ?? 'text');
    const opts = fieldOptions[c.field] ?? [];
    const showSetChecks = SET_OPS.has(c.op) && opts.length > 0;
    const selected = new Set(Array.isArray(c.value) ? (c.value as string[]) : []);
    return (
      <div className={styles.filterRow}>
        <select className={styles.filterField} value={c.field}
                onChange={(e) => {
                  const nc = colByKey.get(e.target.value);
                  update({ ...c, field: e.target.value, op: operatorsForType(nc?.field_type ?? 'text')[0], value: '' });
                }}>
          {columns.map((col2) => <option key={col2.field_key} value={col2.field_key}>{col2.label}</option>)}
        </select>
        <select className={styles.filterOp} value={c.op}
                onChange={(e) => update({ ...c, op: e.target.value as FilterOp, value: SET_OPS.has(e.target.value as FilterOp) ? [] : '' })}>
          {ops.map((op) => <option key={op} value={op}>{OP_LABEL[op]}</option>)}
        </select>
        {!NO_VALUE_OPS.has(c.op) && (
          c.op === 'between' ? (
            <span className={styles.filterBetween}>
              {/* a date column's between must use date inputs — number inputs can't hold a date and
                  the evaluator routes date-between through the date comparator (adv-review) */}
              <input className={styles.filterVal} type={col?.field_type === 'date' ? 'date' : 'number'} placeholder="min"
                     value={(c.value as { a?: string })?.a ?? ''}
                     onChange={(e) => update({ ...c, value: { ...(c.value as object), a: e.target.value } })} />
              <input className={styles.filterVal} type={col?.field_type === 'date' ? 'date' : 'number'} placeholder="max"
                     value={(c.value as { b?: string })?.b ?? ''}
                     onChange={(e) => update({ ...c, value: { ...(c.value as object), b: e.target.value } })} />
            </span>
          ) : showSetChecks ? (
            <div className={styles.filterChecks}>
              {opts.map((o) => (
                <label key={o} className={styles.filterCheck}>
                  <input type="checkbox" checked={selected.has(o)}
                         onChange={() => {
                           const next = new Set(selected);
                           if (next.has(o)) next.delete(o); else next.add(o);
                           update({ ...c, value: [...next] });
                         }} />
                  {/* W5 §10.3: the DISPLAY vocabulary, from the column's server-owned
                      `enum_labels`. The stored condition value stays the RAW one (`update` above is
                      untouched), so a filter built here matches a filter built in the column menu
                      and a saved tree keeps working. Without this the builder offered "AI proposed"
                      for a column whose cells read "Proposed" — two vocabularies for one value in
                      one session (review findings 8/12). */}
                  <span>{enumDisplay(col, o)}</span>
                </label>
              ))}
            </div>
          ) : (
            <input
              className={styles.filterVal}
              type={col?.field_type === 'date' ? 'date' : (col && ['number', 'currency'].includes(col.field_type) ? 'number' : 'text')}
              placeholder={SET_OPS.has(c.op) ? 'comma-separated…' : 'value…'}
              value={typeof c.value === 'string' ? c.value : ''}
              onChange={(e) => update({ ...c, value: e.target.value })}
            />
          )
        )}
        <button className={styles.filterRemove} aria-label="Remove condition" onClick={remove}>×</button>
      </div>
    );
  };

  return (
    <div className={styles.filterPanel}>
      <div className={styles.filterMatch}>
        Match{' '}
        <select className={styles.filterLogic} value={tree.logic} onChange={(e) => setRootLogic(e.target.value as 'and' | 'or')}>
          <option value="and">all</option>
          <option value="or">any</option>
        </select>{' '}
        of:
      </div>

      {tree.children.map((child, i) => (
        child.kind === 'cond' ? (
          <div key={i}>
            {renderCondition(child,
              () => mutate((t) => t.children.splice(i, 1)),
              (next) => mutate((t) => { t.children[i] = next; }))}
          </div>
        ) : (
          <div key={i} className={styles.filterGroup}>
            <div className={styles.filterMatch}>
              <span className={styles.filterGroupTag}>group</span> Match{' '}
              <select className={styles.filterLogic} value={child.logic}
                      onChange={(e) => mutate((t) => { (t.children[i] as FilterGroup).logic = e.target.value as 'and' | 'or'; })}>
                <option value="and">all</option>
                <option value="or">any</option>
              </select>{' '}of:
              <button className={styles.filterRemove} aria-label="Remove group"
                      onClick={() => mutate((t) => t.children.splice(i, 1))}>×</button>
            </div>
            {child.children.map((gc, j) => (
              <div key={j}>
                {renderCondition(gc as FilterCondition,
                  () => mutate((t) => (t.children[i] as FilterGroup).children.splice(j, 1)),
                  (next) => mutate((t) => { (t.children[i] as FilterGroup).children[j] = next; }))}
              </div>
            ))}
            <button className={styles.filterAdd}
                    onClick={() => mutate((t) => (t.children[i] as FilterGroup).children.push(newCondition()))}>+ Add filter</button>
          </div>
        )
      ))}

      <div className={styles.filterActions}>
        <button className={styles.filterAdd} onClick={() => mutate((t) => t.children.push(newCondition()))}>+ Add filter</button>
        <button className={styles.filterAdd} onClick={() => mutate((t) => t.children.push(emptyGroup()))}>+ Add grouping</button>
        {tree.children.length > 0 && (
          <button className={styles.filterClear} onClick={() => onChange(emptyGroup(tree.logic))}>Clear all</button>
        )}
      </div>
    </div>
  );
}
